import { useEffect, useState } from "react";
import Form from "../../../components/form/Form";
import { useHub } from "../../../core/Global";
import { toastSuccess } from "../../../components/base/Toast";
import Icon from "../../../components/base/Icon";
import ToggleSwitch from "../../../components/form/ToggleSwitch";
import FlexRow from "../../../components/base/FlexRow";
import Indicator from "../../../components/base/Indicator";
import TimePicker from "../../../components/form/TimePicker";
import { API_ROUTES } from "../../../core/Constants";

/**
 * When a technician is clocked in and out on their own, day by day — the same setting the
 * technician has in the app's Account tab. Whoever saved it last is named on it, here and there.
 *
 * Three ways to fill it in, as in the app: one pair of times for every day, one for Monday to
 * Friday, or each day its own. The days are what is stored either way; the way it was filled in
 * is kept so the form opens as it was left.
 */

type Mode = "everyday" | "weekdays" | "custom"

interface ScheduleDay {
    dayOfWeek: number
    enabled: boolean
    clockIn?: string
    clockOut?: string
}

interface Schedule {
    enabled: boolean
    mode: Mode
    days: ScheduleDay[]
    timezoneId?: string
    timezoneName?: string
    updatedAt?: string
    updatedByName?: string
    updatedBySelf?: boolean
    canEdit?: boolean
}

const modes: { id: Mode, name: string }[] = [
    { id: "everyday", name: "Every Day" },
    { id: "weekdays", name: "Weekdays" },
    { id: "custom", name: "Day by Day" }
]

const dayNames: Record<number, string> = {
    0: "Sunday", 1: "Monday", 2: "Tuesday", 3: "Wednesday", 4: "Thursday", 5: "Friday", 6: "Saturday"
}

/** "HH:mm" as minutes since midnight, or nothing when it is not a time. */
const minutesOf = (value?: string) => {
    const parts = (value ?? "").split(":").map(p => parseInt(p, 10))
    if (parts.length !== 2 || parts.some(isNaN)) return undefined
    return parts[0] * 60 + parts[1]
}

const ordered = (start?: string, end?: string) => {
    const a = minutesOf(start), b = minutesOf(end)
    return a !== undefined && b !== undefined && b > a
}

/** -1 is the shared pair of times; 0–6 a day. */
type Slot = number

interface IProps {
    userId?: string
}

export default function AccountTechAutoClock(props: IProps) {

    const hub = useHub()

    const [model, modelSet] = useState<Schedule>()
    const [sharedIn, sharedInSet] = useState<string>("08:00")
    const [sharedOut, sharedOutSet] = useState<string>("17:00")
    const [invalid, invalidSet] = useState<Set<Slot>>(new Set())
    const [working, workingSet] = useState<boolean>(false)

    const load = () => {
        if (!props.userId) return

        hub.Get(`${API_ROUTES.Technicians}clockschedule/${props.userId}`, res => {
            if (!res.success) return

            const schedule: Schedule = res.data
            modelSet(schedule)

            // The shared pair is whatever the first day that is on says.
            const first = schedule.days.find(d => d.enabled) ?? schedule.days[0]
            sharedInSet(first?.clockIn ?? "08:00")
            sharedOutSet(first?.clockOut ?? "17:00")
        })
    }

    useEffect(load, [props.userId])

    if (!model) {
        return (
            <div className="card mb-5 mb-xl-10">
                <div className="card-body p-20 text-center"><Indicator /></div>
            </div>
        )
    }

    const readOnly = !model.canEdit

    const unmark = (slot: Slot) => invalidSet(prev => {
        if (!prev.has(slot)) return prev
        const next = new Set(prev)
        next.delete(slot)
        return next
    })

    /** The seven days the form means, whichever way it was filled in. */
    const resolved = (mode: Mode): ScheduleDay[] => mode === "custom"
        ? model.days
        : model.days.map(d => ({
            ...d,
            enabled: mode === "everyday" || (d.dayOfWeek >= 1 && d.dayOfWeek <= 5),
            clockIn: sharedIn,
            clockOut: sharedOut
        }))

    /** Moving between the ways of filling it in carries the times along. */
    const switchMode = (to: Mode) => {
        if (to === model.mode) return
        invalidSet(new Set())

        if (to === "custom") {
            modelSet({ ...model, mode: to, days: resolved(model.mode) })
            return
        }

        if (model.mode === "custom") {
            const first = model.days.find(d => d.enabled) ?? model.days[0]
            if (first?.clockIn) sharedInSet(first.clockIn)
            if (first?.clockOut) sharedOutSet(first.clockOut)
        }

        modelSet({ ...model, mode: to })
    }

    const setDay = (dayOfWeek: number, change: Partial<ScheduleDay>) => {
        modelSet({ ...model, days: model.days.map(d => d.dayOfWeek === dayOfWeek ? { ...d, ...change } : d) })
        unmark(dayOfWeek)
    }

    const save = () => {
        const week = resolved(model.mode)

        // Clock-out after clock-in, said by turning the times red rather than in words.
        const wrong = new Set<Slot>()
        if (model.mode === "custom") {
            week.filter(d => d.enabled && !ordered(d.clockIn, d.clockOut)).forEach(d => wrong.add(d.dayOfWeek))
        }
        else if (!ordered(sharedIn, sharedOut)) {
            wrong.add(-1)
        }

        // Switched on with every day off: the days are what is missing.
        if (model.enabled && !week.some(d => d.enabled)) week.forEach(d => wrong.add(d.dayOfWeek))

        invalidSet(wrong)
        if (wrong.size > 0) return

        workingSet(true)

        hub.Put(`${API_ROUTES.Technicians}clockschedule/${props.userId}`, {
            enabled: model.enabled,
            mode: model.mode,
            days: week
        }, res => {
            workingSet(false)
            if (res.success) {
                toastSuccess("Auto clock in & out has been updated.")
                load()
            }
        })
    }

    const changedBy = model.updatedByName
        ? `Last changed by ${model.updatedBySelf ? `${model.updatedByName} in the app` : model.updatedByName}`
            + (model.updatedAt ? `, ${new Date(model.updatedAt.endsWith("Z") ? model.updatedAt : model.updatedAt + "Z").toLocaleString()}` : "")
        : undefined

    const timeField = (id: string, value: string | undefined, slot: Slot, placeholder: string, onPick: (v: string) => void) => (
        <div className="w-150px me-2">
            <TimePicker id={id}
                disabled={readOnly}
                noMarginBottom
                placeholder={placeholder}
                value={value}
                className={invalid.has(slot) ? "is-invalid" : undefined}
                onTimePicked={t => { onPick(t.label); unmark(slot) }}
                onClear={() => onPick("")}
            />
        </div>
    )

    return (
        <Form id="user-tech-autoclock" className="form" onSubmit={save}>
            <div className="card mb-5 mb-xl-10">
                <div className="card-header border-0">
                    <div className="card-title m-0">
                        <h3 className="fw-bolder m-0">Auto Clock In & Out</h3>
                    </div>
                    {
                        !readOnly &&
                        <div className="card-action">
                            <button type="submit" className="btn btn-sm btn-primary" disabled={working}>
                                {
                                    working
                                        ? <><Icon icon="spinner-third" spin /><span className="ms-2">Updating...</span></>
                                        : <>Update Auto Clock</>
                                }
                            </button>
                        </div>
                    }
                </div>

                <div className="card-body border-top p-9" style={{ maxWidth: 1000 }}>

                    <ToggleSwitch label="Clock this technician in and out automatically"
                        raw small className="mb-2"
                        disabled={readOnly}
                        checked={model.enabled}
                        onChange={e => modelSet({ ...model, enabled: e })} />

                    <div className="fs-7 text-gray-600 mb-8">
                        Times are in the technician's time zone{model.timezoneName || model.timezoneId ? `: ${model.timezoneName ?? model.timezoneId}` : ""}.
                    </div>

                    <div className="btn-group mb-8" role="group">
                        {
                            modes.map(m =>
                                <button key={m.id} type="button" disabled={readOnly}
                                    className={`btn btn-sm ${model.mode === m.id ? "btn-primary" : "btn-light"}`}
                                    onClick={() => switchMode(m.id)}>
                                    {m.name}
                                </button>
                            )
                        }
                    </div>

                    {
                        model.mode === "custom"
                            ? [1, 2, 3, 4, 5, 6, 0].map(dayOfWeek => {
                                const d = model.days.find(x => x.dayOfWeek === dayOfWeek)
                                if (!d) return null

                                return (
                                    <FlexRow key={`autoclock-day-${dayOfWeek}`} className="border-top pt-5 pb-1">
                                        <div className="w-150px">
                                            <ToggleSwitch label={dayNames[dayOfWeek]}
                                                raw small className={"mb-5" + (invalid.has(dayOfWeek) ? " text-danger" : "")}
                                                disabled={readOnly}
                                                checked={d.enabled}
                                                onChange={e => setDay(dayOfWeek, { enabled: e })} />
                                        </div>
                                        {
                                            d.enabled &&
                                            <>
                                                {timeField(`autoclock-${dayOfWeek}-in`, d.clockIn, dayOfWeek, "Clock in", v => setDay(dayOfWeek, { clockIn: v }))}
                                                {timeField(`autoclock-${dayOfWeek}-out`, d.clockOut, dayOfWeek, "Clock out", v => setDay(dayOfWeek, { clockOut: v }))}
                                            </>
                                        }
                                    </FlexRow>
                                )
                            })
                            : <>
                                <FlexRow>
                                    <div className="w-150px fw-semibold">Clock in – out</div>
                                    {timeField("autoclock-shared-in", sharedIn, -1, "Clock in", sharedInSet)}
                                    {timeField("autoclock-shared-out", sharedOut, -1, "Clock out", sharedOutSet)}
                                </FlexRow>
                                <div className="fs-7 text-gray-600 mt-3">
                                    {model.mode === "weekdays" ? "Monday to Friday. Saturday and Sunday off." : "All seven days of the week."}
                                </div>
                            </>
                    }

                    {
                        changedBy &&
                        <div className="fs-8 text-gray-500 mt-10">{changedBy}.</div>
                    }

                    {
                        readOnly &&
                        <div className="fs-8 text-gray-500 mt-2">Only the account owner and admins can change this.</div>
                    }
                </div>
            </div>
        </Form>
    )
}

